import { useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

function ConfirmModal({ 
  onConfirm, 
  onCancel, 
  title = "Leave Chat?", 
  message = "Are you sure you want to leave the chat? This will disconnect you from the conversation and you'll need to enter your nickname again to rejoin.",
  confirmText = "Leave Chat",
  cancelText = "Cancel",
  toggleText = "Yes, I want to leave this chat"
}) {
  const [enabled, setEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = () => {
    if (enabled) {
      setIsLoading(true);
      // Add a small delay to show the loading state
      setTimeout(() => {
        onConfirm();
      }, 600);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-2xl p-6 sm:p-8 w-full max-w-md shadow-2xl dark:shadow-black/30 animate-bounce-in">
        {/* Header with warning icon */}
        <div className="flex items-center gap-4 mb-6">
          <div className="p-3 bg-red-900/30 rounded-full">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-400" />
          </div>
          <h3 className="text-xl font-bold text-white">
            {title}
          </h3>
          <button 
            onClick={onCancel}
            className="ml-auto p-2 rounded-full hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        
        {/* Description */}
        <div className="mb-8">
          <p className="text-gray-300 mb-4">
            {message}
          </p>
          
          {/* Confirmation toggle */}
          <div 
            onClick={() => setEnabled(!enabled)}
            className={`flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all
              ${enabled 
                ? 'bg-red-900/20 border-2 border-red-800' 
                : 'bg-gray-700 border border-gray-600'}`}
          >
            <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors
              ${enabled 
                ? 'bg-red-600' 
                : 'border-2 border-gray-500 bg-gray-800'}`}>
              {enabled && <CheckCircleIcon className="h-4 w-4 text-white" />}
            </div>
            <span className={`font-medium ${enabled 
              ? 'text-red-400' 
              : 'text-gray-300'}`}>
              {toggleText}
            </span>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
          <button
            onClick={onCancel}
            className="px-5 py-3 text-gray-300 hover:bg-gray-700 rounded-xl font-medium transition-colors order-2 sm:order-1"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={!enabled || isLoading}
            className={`px-5 py-3 rounded-xl font-medium transition-all flex items-center justify-center gap-2 order-1 sm:order-2
              ${!enabled 
                ? 'bg-gray-700 text-gray-500 cursor-not-allowed' 
                : isLoading
                  ? 'bg-red-500 text-white cursor-wait'
                  : 'bg-red-600 text-white hover:bg-red-700 hover:shadow-lg hover:shadow-red-600/30'}`}
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Leaving...
              </>
            ) : (
              <>
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                {confirmText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmModal; 